// ============================================================================
// Admin Compliance Dashboard
// Central hub for monitoring and managing all employee compliance activities
// ============================================================================

import React, { useState, useEffect, useMemo } from "react";
import { toast } from "sonner";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Separator } from "@/components/ui/separator";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  CheckCircle2,
  Clock,
  AlertTriangle,
  XCircle,
  Users,
  FileText,
  Shield,
  Calendar,
  Search,
  Filter,
  Download,
  Eye,
  Edit,
  MoreHorizontal,
  RefreshCw,
  Bell,
  TrendingUp,
  TrendingDown,
  FileCheck,
  ClipboardCheck,
  GraduationCap,
  Building2,
  ChevronRight,
  BarChart3,
  PieChart,
  Activity,
  Inbox,
  UserCheck,
  FileWarning,
  Scale,
  AlertCircle,
  CheckSquare,
  History,
  DollarSign,
  Send,
  Plus,
  Trash2,
  Copy,
  Mail,
} from "lucide-react";
import { db } from "@/lib/db";
import { useLiveQuery } from "dexie-react-hooks";
import { I9_STATUS } from "@/lib/complianceData";
import FormW4 from "@/components/shared/compliance/FormW4";
import FormI9 from "@/components/shared/compliance/FormI9";
import { COMPANY_POLICIES } from "@/lib/policyTemplates";
import { generateW4PdfFromTemplate } from "@/lib/w4PdfGenerator";
import { getStateTaxRequirements } from "@/lib/complianceData";

// Map I9_STATUS to SUBMISSION_STATUS for compatibility
const SUBMISSION_STATUS = I9_STATUS;

// ============================================================================
// COMPLIANCE STATISTICS CARD
// ============================================================================

function StatCard({
  title,
  value,
  subtitle,
  icon: Icon,
  trend,
  trendValue,
  color = "blue",
  onClick,
}) {
  const colorClasses = {
    blue: "bg-blue-100 text-blue-600",
    green: "bg-green-100 text-green-600",
    yellow: "bg-yellow-100 text-yellow-600",
    red: "bg-red-100 text-red-600",
    purple: "bg-purple-100 text-purple-600",
    orange: "bg-orange-100 text-orange-600",
  };

  return (
    <Card
      className={`transition-all hover:shadow-md ${
        onClick ? "cursor-pointer" : ""
      }`}
      onClick={onClick}
    >
      <CardContent className="pt-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-500">{title}</p>
            <h3 className="text-3xl font-bold text-gray-900 mt-1">{value}</h3>
            {subtitle && (
              <p className="text-sm text-gray-500 mt-1">{subtitle}</p>
            )}
            {trend && (
              <div
                className={`flex items-center gap-1 mt-2 text-sm ${
                  trend === "up" ? "text-green-600" : "text-red-600"
                }`}
              >
                {trend === "up" ? (
                  <TrendingUp className="h-4 w-4" />
                ) : (
                  <TrendingDown className="h-4 w-4" />
                )}
                <span>{trendValue}</span>
              </div>
            )}
          </div>
          <div
            className={`w-12 h-12 rounded-full flex items-center justify-center ${colorClasses[color]}`}
          >
            <Icon className="h-6 w-6" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================================================
// I-9 RE-VERIFICATION ALERTS
// ============================================================================

function I9ReverificationAlerts({ i9Submissions, employees, onReview }) {
  const alertsData = useMemo(() => {
    const today = new Date();
    const thirtyDaysFromNow = new Date(today);
    thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);
    const sixtyDaysFromNow = new Date(today);
    sixtyDaysFromNow.setDate(sixtyDaysFromNow.getDate() + 60);
    const ninetyDaysFromNow = new Date(today);
    ninetyDaysFromNow.setDate(ninetyDaysFromNow.getDate() + 90);

    return (
      i9Submissions
        ?.filter((s) => s.status === "complete")
        ?.map((s) => {
          const employee = employees?.find((e) => e.id === s.userId);
          const data = s.data || s;
          const citizenshipStatus =
            data.citizenshipStatus || data.section1?.citizenshipStatus;

          // Check for work authorization expiration (for non-citizens/non-LPRs)
          let expirationDate = null;
          let documentTitle = "";

          if (
            citizenshipStatus === "noncitizen_authorized" ||
            citizenshipStatus === "alien_authorized"
          ) {
            expirationDate =
              data.workAuthExpiration || data.section1?.workAuthExpiration;
            documentTitle = "Work Authorization";
          }

          // Also check Section 2 document expirations
          const section2 = s.section2;
          if (section2?.listADocument?.expiration) {
            const listAExp = new Date(section2.listADocument.expiration);
            if (!expirationDate || listAExp < new Date(expirationDate)) {
              expirationDate = section2.listADocument.expiration;
              documentTitle = section2.listADocument.title || "List A Document";
            }
          }
          if (section2?.listBDocument?.expiration) {
            const listBExp = new Date(section2.listBDocument.expiration);
            if (!expirationDate || listBExp < new Date(expirationDate)) {
              expirationDate = section2.listBDocument.expiration;
              documentTitle = section2.listBDocument.title || "List B Document";
            }
          }

          if (!expirationDate) return null;

          const expDate = new Date(expirationDate);
          const daysUntilExpiration = Math.ceil(
            (expDate - today) / (1000 * 60 * 60 * 24),
          );

          let urgency = "none";
          if (daysUntilExpiration <= 0) urgency = "expired";
          else if (daysUntilExpiration <= 30) urgency = "critical";
          else if (daysUntilExpiration <= 60) urgency = "warning";
          else if (daysUntilExpiration <= 90) urgency = "upcoming";
          else return null;

          return {
            ...s,
            employee,
            expirationDate,
            documentTitle,
            daysUntilExpiration,
            urgency,
          };
        })
        ?.filter(Boolean)
        ?.sort((a, b) => a.daysUntilExpiration - b.daysUntilExpiration) || []
    );
  }, [i9Submissions, employees]);

  if (alertsData.length === 0) return null;

  const getUrgencyColor = (urgency) => {
    switch (urgency) {
      case "expired":
        return "bg-red-50 border-red-200 text-red-800";
      case "critical":
        return "bg-red-50 border-red-200 text-red-700";
      case "warning":
        return "bg-yellow-50 border-yellow-200 text-yellow-800";
      case "upcoming":
        return "bg-blue-50 border-blue-200 text-blue-700";
      default:
        return "bg-gray-50 border-gray-200 text-gray-700";
    }
  };

  return (
    <Card className="border-orange-200">
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
            <AlertCircle className="h-5 w-5 text-orange-600" />
          </div>
          <div>
            <CardTitle className="text-lg">
              I-9 Re-verification Alerts
            </CardTitle>
            <CardDescription>
              Work authorizations requiring re-verification
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {alertsData.slice(0, 5).map((alert) => (
            <Alert key={alert.id} className={getUrgencyColor(alert.urgency)}>
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription className="flex items-center justify-between">
                <div>
                  <span className="font-medium">
                    {alert.employee?.profile?.firstName}{" "}
                    {alert.employee?.profile?.lastName}
                  </span>
                  <span className="mx-2">-</span>
                  <span>{alert.documentTitle}</span>
                  <span className="mx-2">•</span>
                  <span>
                    {alert.urgency === "expired"
                      ? `Expired ${Math.abs(
                          alert.daysUntilExpiration,
                        )} days ago`
                      : `Expires in ${alert.daysUntilExpiration} days`}
                  </span>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onReview?.(alert)}
                >
                  Review
                </Button>
              </AlertDescription>
            </Alert>
          ))}
        </div>
        {alertsData.length > 5 && (
          <p className="text-sm text-gray-500 mt-3 text-center">
            +{alertsData.length - 5} more alerts
          </p>
        )}
      </CardContent>
    </Card>
  );
}

// ============================================================================
// CA NOTICES TRACKER
// ============================================================================

function CANoticesTracker({ submissions, employees }) {
  const stats = useMemo(() => {
    const caNoticesSubmissions =
      submissions?.filter((s) => s.formType === "CA_NOTICES") || [];
    const complete = caNoticesSubmissions.filter(
      (s) => s.status === "complete",
    ).length;
    const total = employees?.filter((e) => e.status === "active")?.length || 0;
    const pending = total - complete;
    const percentage = total > 0 ? Math.round((complete / total) * 100) : 0;

    return {
      complete,
      total,
      pending,
      percentage,
      submissions: caNoticesSubmissions,
    };
  }, [submissions, employees]);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
            <Building2 className="h-5 w-5 text-red-600" />
          </div>
          <div>
            <CardTitle className="text-lg">California Notices</CardTitle>
            <CardDescription>
              State-required workplace notice acknowledgments
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">
              Acknowledgments Complete
            </span>
            <span className="font-semibold">
              {stats.complete}/{stats.total}
            </span>
          </div>
          <Progress value={stats.percentage} className="h-2" />
          {stats.pending > 0 && (
            <Alert className="bg-yellow-50 border-yellow-200">
              <AlertTriangle className="h-4 w-4 text-yellow-600" />
              <AlertDescription className="text-yellow-800 text-sm">
                {stats.pending} employee{stats.pending !== 1 ? "s" : ""} have
                not acknowledged CA workplace notices
              </AlertDescription>
            </Alert>
          )}
        </div>
      </CardContent>
      <CardFooter>
        <Button variant="outline" size="sm" className="w-full">
          <Bell className="h-4 w-4 mr-2" />
          Send Reminder to Pending
        </Button>
      </CardFooter>
    </Card>
  );
}

// ============================================================================
// WTPN MANAGEMENT (Issue new WTPN for pay changes)
// ============================================================================

function WTPNManager({ employees, submissions, onIssueWTPN }) {
  const [showIssueDialog, setShowIssueDialog] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [newWTPNData, setNewWTPNData] = useState({
    reason: "pay_change",
    newPayRate: "",
    effectiveDate: new Date().toISOString().split("T")[0],
    notes: "",
  });

  const wtpnStats = useMemo(() => {
    const wtpnSubmissions =
      submissions?.filter((s) => s.formType === "WTPN") || [];
    const pendingReissue = []; // Track employees who might need new WTPN

    employees?.forEach((emp) => {
      const existingWTPN = wtpnSubmissions.find(
        (w) => w.userId === emp.id && w.status === "complete",
      );
      if (existingWTPN) {
        // Check if pay rate has changed since WTPN was issued
        const currentPayRate =
          emp.profile?.payRate ||
          emp.profile?.hourlyRate ||
          emp.profile?.salary;
        const wtpnPayRate = existingWTPN.data?.rateOfPay;
        if (currentPayRate && wtpnPayRate && currentPayRate !== wtpnPayRate) {
          pendingReissue.push({
            employee: emp,
            existingWTPN,
            currentPayRate,
            wtpnPayRate,
          });
        }
      }
    });

    return {
      total: wtpnSubmissions.length,
      complete: wtpnSubmissions.filter((s) => s.status === "complete").length,
      pendingReissue,
    };
  }, [employees, submissions]);

  const handleIssueWTPN = async () => {
    if (!selectedEmployee) return;

    const wtpnData = {
      id: `WTPN_${selectedEmployee.id}_${Date.now()}`,
      formType: "WTPN",
      userId: selectedEmployee.id,
      status: "pending_acknowledgment",
      issuedBy: "HR Admin",
      issuedAt: new Date().toISOString(),
      reason: newWTPNData.reason,
      data: {
        employeeName: `${selectedEmployee.profile?.firstName} ${selectedEmployee.profile?.lastName}`,
        rateOfPay: newWTPNData.newPayRate,
        effectiveDate: newWTPNData.effectiveDate,
        payBasis: selectedEmployee.profile?.payType || "hourly",
        previousRate: selectedEmployee.profile?.payRate,
      },
      createdAt: new Date().toISOString(),
    };

    await onIssueWTPN?.(wtpnData);
    setShowIssueDialog(false);
    setSelectedEmployee(null);
    setNewWTPNData({
      reason: "pay_change",
      newPayRate: "",
      effectiveDate: new Date().toISOString().split("T")[0],
      notes: "",
    });
  };

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                <DollarSign className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <CardTitle className="text-lg">
                  Wage Theft Prevention Notices
                </CardTitle>
                <CardDescription>
                  Track and issue WTPN for pay rate changes
                </CardDescription>
              </div>
            </div>
            <Button size="sm" onClick={() => setShowIssueDialog(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Issue New WTPN
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {wtpnStats.pendingReissue.length > 0 && (
            <Alert className="bg-orange-50 border-orange-200 mb-4">
              <AlertTriangle className="h-4 w-4 text-orange-600" />
              <AlertTitle className="text-orange-800">
                Pay Rate Changes Detected
              </AlertTitle>
              <AlertDescription className="text-orange-700 text-sm">
                {wtpnStats.pendingReissue.length} employee
                {wtpnStats.pendingReissue.length !== 1 ? "s" : ""} may need
                updated WTPN due to pay rate changes.
              </AlertDescription>
            </Alert>
          )}

          {wtpnStats.pendingReissue.length > 0 && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Employee</TableHead>
                  <TableHead>Previous Rate</TableHead>
                  <TableHead>Current Rate</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {wtpnStats.pendingReissue.slice(0, 5).map((item) => (
                  <TableRow key={item.employee.id}>
                    <TableCell>
                      {item.employee.profile?.firstName}{" "}
                      {item.employee.profile?.lastName}
                    </TableCell>
                    <TableCell>${item.wtpnPayRate}</TableCell>
                    <TableCell className="font-medium text-green-600">
                      ${item.currentPayRate}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setSelectedEmployee(item.employee);
                          setNewWTPNData((prev) => ({
                            ...prev,
                            newPayRate: item.currentPayRate,
                          }));
                          setShowIssueDialog(true);
                        }}
                      >
                        Issue WTPN
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Issue WTPN Dialog */}
      <Dialog open={showIssueDialog} onOpenChange={setShowIssueDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Issue Wage Theft Prevention Notice</DialogTitle>
            <DialogDescription>
              Issue a new WTPN for pay rate changes or new hire
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Select Employee</Label>
              <Select
                value={selectedEmployee?.id || ""}
                onValueChange={(val) =>
                  setSelectedEmployee(employees?.find((e) => e.id === val))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Choose employee..." />
                </SelectTrigger>
                <SelectContent>
                  {employees
                    ?.filter((e) => e.status === "active")
                    ?.map((emp) => (
                      <SelectItem key={emp.id} value={emp.id}>
                        {emp.profile?.firstName} {emp.profile?.lastName}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Reason</Label>
              <Select
                value={newWTPNData.reason}
                onValueChange={(val) =>
                  setNewWTPNData((prev) => ({ ...prev, reason: val }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pay_change">Pay Rate Change</SelectItem>
                  <SelectItem value="new_hire">New Hire</SelectItem>
                  <SelectItem value="position_change">
                    Position Change
                  </SelectItem>
                  <SelectItem value="annual_update">Annual Update</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>New Pay Rate ($)</Label>
              <Input
                type="number"
                step="0.01"
                value={newWTPNData.newPayRate}
                onChange={(e) =>
                  setNewWTPNData((prev) => ({
                    ...prev,
                    newPayRate: e.target.value,
                  }))
                }
                placeholder="25.00"
              />
            </div>
            <div>
              <Label>Effective Date</Label>
              <Input
                type="date"
                value={newWTPNData.effectiveDate}
                onChange={(e) =>
                  setNewWTPNData((prev) => ({
                    ...prev,
                    effectiveDate: e.target.value,
                  }))
                }
              />
            </div>
            <div>
              <Label>Notes (Optional)</Label>
              <Textarea
                value={newWTPNData.notes}
                onChange={(e) =>
                  setNewWTPNData((prev) => ({ ...prev, notes: e.target.value }))
                }
                placeholder="Additional notes..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowIssueDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleIssueWTPN}
              disabled={!selectedEmployee || !newWTPNData.newPayRate}
            >
              <Send className="h-4 w-4 mr-2" />
              Issue WTPN
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ============================================================================
// FORM UPDATE REQUESTS (W-4/DE-4)
// ============================================================================

function FormUpdateRequests({ employees, submissions, onRequestUpdate }) {
  const [showRequestDialog, setShowRequestDialog] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [requestData, setRequestData] = useState({
    formType: "W-4",
    reason: "",
    message: "",
  });

  const lifeEventReasons = [
    { value: "marriage", label: "Marriage/Divorce" },
    { value: "child", label: "Birth/Adoption of Child" },
    { value: "job_change", label: "Second Job Change" },
    { value: "income_change", label: "Income Change" },
    { value: "annual_review", label: "Annual Review" },
    { value: "other", label: "Other" },
  ];

  const handleRequestUpdate = async () => {
    if (!selectedEmployee) return;

    const request = {
      id: `request_${Date.now()}`,
      type: "form_update_request",
      formType: requestData.formType,
      userId: selectedEmployee.id,
      employeeName: `${selectedEmployee.profile?.firstName} ${selectedEmployee.profile?.lastName}`,
      reason: requestData.reason,
      message: requestData.message,
      status: "pending",
      requestedBy: "HR Admin",
      requestedAt: new Date().toISOString(),
    };

    await onRequestUpdate?.(request);
    setShowRequestDialog(false);
    setSelectedEmployee(null);
    setRequestData({ formType: "W-4", reason: "", message: "" });
  };

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                <Edit className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <CardTitle className="text-lg">Request Form Updates</CardTitle>
                <CardDescription>
                  Request employees to update W-4 or DE-4 forms
                </CardDescription>
              </div>
            </div>
            <Button size="sm" onClick={() => setShowRequestDialog(true)}>
              <Mail className="h-4 w-4 mr-2" />
              Request Update
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-600">
            Request employees to update their tax withholding forms due to life
            events, annual reviews, or other reasons.
          </p>
        </CardContent>
      </Card>

      <Dialog open={showRequestDialog} onOpenChange={setShowRequestDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Request Form Update</DialogTitle>
            <DialogDescription>
              Send a request to an employee to update their tax forms
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Select Employee</Label>
              <Select
                value={selectedEmployee?.id || ""}
                onValueChange={(val) =>
                  setSelectedEmployee(employees?.find((e) => e.id === val))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Choose employee..." />
                </SelectTrigger>
                <SelectContent>
                  {employees
                    ?.filter((e) => e.status === "active")
                    ?.map((emp) => (
                      <SelectItem key={emp.id} value={emp.id}>
                        {emp.profile?.firstName} {emp.profile?.lastName}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Form Type</Label>
              <Select
                value={requestData.formType}
                onValueChange={(val) =>
                  setRequestData((prev) => ({ ...prev, formType: val }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="W-4">Form W-4 (Federal Tax)</SelectItem>
                  <SelectItem value="DE-4">Form DE-4 (CA State Tax)</SelectItem>
                  <SelectItem value="both">Both W-4 and DE-4</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Reason</Label>
              <Select
                value={requestData.reason}
                onValueChange={(val) =>
                  setRequestData((prev) => ({ ...prev, reason: val }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select reason..." />
                </SelectTrigger>
                <SelectContent>
                  {lifeEventReasons.map((reason) => (
                    <SelectItem key={reason.value} value={reason.value}>
                      {reason.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Message to Employee</Label>
              <Textarea
                value={requestData.message}
                onChange={(e) =>
                  setRequestData((prev) => ({
                    ...prev,
                    message: e.target.value,
                  }))
                }
                placeholder="Please review and update your tax withholding information..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowRequestDialog(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleRequestUpdate}
              disabled={!selectedEmployee || !requestData.reason}
            >
              <Send className="h-4 w-4 mr-2" />
              Send Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ============================================================================
// BULK REMINDER SENDER
// ============================================================================

function BulkReminderSender({
  employees,
  formSubmissions,
  i9Submissions,
  policyAcks,
  onSendReminders,
}) {
  const [showDialog, setShowDialog] = useState(false);
  const [reminderType, setReminderType] = useState("all_incomplete");
  const [selectedFormType, setSelectedFormType] = useState("all");
  const [isSending, setIsSending] = useState(false);

  const incompleteStats = useMemo(() => {
    const stats = {
      i9: [],
      w4: [],
      de4: [],
      wtpn: [],
      caNotices: [],
      policies: [],
    };

    employees
      ?.filter((e) => e.status === "active")
      ?.forEach((emp) => {
        const empI9 = i9Submissions?.find((s) => s.userId === emp.id);
        const empW4 = formSubmissions?.find(
          (s) =>
            s.userId === emp.id &&
            s.formType === "W-4" &&
            s.status === "complete",
        );
        const empDE4 = formSubmissions?.find(
          (s) =>
            s.userId === emp.id &&
            s.formType === "DE-4" &&
            s.status === "complete",
        );
        const empWTPN = formSubmissions?.find(
          (s) =>
            s.userId === emp.id &&
            s.formType === "WTPN" &&
            s.status === "complete",
        );
        const empCANotices = formSubmissions?.find(
          (s) =>
            s.userId === emp.id &&
            s.formType === "CA_NOTICES" &&
            s.status === "complete",
        );
        const empPolicies = policyAcks?.filter(
          (a) => a.userId === emp.id && a.status === "acknowledged",
        );

        if (!empI9 || empI9.status !== "complete") stats.i9.push(emp);
        if (!empW4) stats.w4.push(emp);
        if (!empDE4) stats.de4.push(emp);
        if (!empWTPN) stats.wtpn.push(emp);
        if (!empCANotices) stats.caNotices.push(emp);
        if (!empPolicies || empPolicies.length === 0) stats.policies.push(emp);
      });

    return stats;
  }, [employees, formSubmissions, i9Submissions, policyAcks]);

  const getTargetCount = () => {
    if (reminderType === "specific_form") {
      switch (selectedFormType) {
        case "I-9":
          return incompleteStats.i9.length;
        case "W-4":
          return incompleteStats.w4.length;
        case "DE-4":
          return incompleteStats.de4.length;
        case "WTPN":
          return incompleteStats.wtpn.length;
        case "CA_NOTICES":
          return incompleteStats.caNotices.length;
        case "policies":
          return incompleteStats.policies.length;
        default:
          return 0;
      }
    }
    // All incomplete - unique employees who have any incomplete item
    const allIncomplete = new Set([
      ...incompleteStats.i9.map((e) => e.id),
      ...incompleteStats.w4.map((e) => e.id),
      ...incompleteStats.de4.map((e) => e.id),
      ...incompleteStats.wtpn.map((e) => e.id),
      ...incompleteStats.caNotices.map((e) => e.id),
      ...incompleteStats.policies.map((e) => e.id),
    ]);
    return allIncomplete.size;
  };

  const handleSendReminders = async () => {
    setIsSending(true);
    try {
      let targetEmployees = [];

      if (reminderType === "specific_form") {
        switch (selectedFormType) {
          case "I-9":
            targetEmployees = incompleteStats.i9;
            break;
          case "W-4":
            targetEmployees = incompleteStats.w4;
            break;
          case "DE-4":
            targetEmployees = incompleteStats.de4;
            break;
          case "WTPN":
            targetEmployees = incompleteStats.wtpn;
            break;
          case "CA_NOTICES":
            targetEmployees = incompleteStats.caNotices;
            break;
          case "policies":
            targetEmployees = incompleteStats.policies;
            break;
        }
      } else {
        // Get all unique employees with incomplete items
        const uniqueIds = new Set([
          ...incompleteStats.i9.map((e) => e.id),
          ...incompleteStats.w4.map((e) => e.id),
          ...incompleteStats.de4.map((e) => e.id),
          ...incompleteStats.wtpn.map((e) => e.id),
          ...incompleteStats.caNotices.map((e) => e.id),
          ...incompleteStats.policies.map((e) => e.id),
        ]);
        targetEmployees = employees?.filter((e) => uniqueIds.has(e.id)) || [];
      }

      await onSendReminders?.(targetEmployees, reminderType, selectedFormType);
      setShowDialog(false);
    } finally {
      setIsSending(false);
    }
  };

  return (
    <>
      <Button onClick={() => setShowDialog(true)} variant="outline">
        <Bell className="h-4 w-4 mr-2" />
        Send Bulk Reminders
      </Button>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send Compliance Reminders</DialogTitle>
            <DialogDescription>
              Send reminder notifications to employees with incomplete
              compliance items
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Reminder Type</Label>
              <Select value={reminderType} onValueChange={setReminderType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all_incomplete">
                    All Incomplete Items
                  </SelectItem>
                  <SelectItem value="specific_form">
                    Specific Form Type
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {reminderType === "specific_form" && (
              <div>
                <Label>Form Type</Label>
                <Select
                  value={selectedFormType}
                  onValueChange={setSelectedFormType}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="I-9">
                      Form I-9 ({incompleteStats.i9.length} pending)
                    </SelectItem>
                    <SelectItem value="W-4">
                      Form W-4 ({incompleteStats.w4.length} pending)
                    </SelectItem>
                    <SelectItem value="DE-4">
                      Form DE-4 ({incompleteStats.de4.length} pending)
                    </SelectItem>
                    <SelectItem value="WTPN">
                      WTPN ({incompleteStats.wtpn.length} pending)
                    </SelectItem>
                    <SelectItem value="CA_NOTICES">
                      CA Notices ({incompleteStats.caNotices.length} pending)
                    </SelectItem>
                    <SelectItem value="policies">
                      Policies ({incompleteStats.policies.length} pending)
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            <Alert>
              <Mail className="h-4 w-4" />
              <AlertDescription>
                <strong>{getTargetCount()}</strong> employee
                {getTargetCount() !== 1 ? "s" : ""} will receive reminder
                notifications.
              </AlertDescription>
            </Alert>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSendReminders}
              disabled={getTargetCount() === 0 || isSending}
            >
              <Send className="h-4 w-4 mr-2" />
              {isSending
                ? "Sending..."
                : `Send ${getTargetCount()} Reminder${
                    getTargetCount() !== 1 ? "s" : ""
                  }`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ============================================================================
// I-9 SECTION 2 QUEUE
// ============================================================================

function I9ReviewQueue({ submissions, employees, onReview }) {
  const pendingReviews = useMemo(() => {
    return (
      submissions
        ?.filter(
          (s) =>
            s.status === SUBMISSION_STATUS.SECTION1_COMPLETE ||
            s.status === I9_STATUS.SECTION1_COMPLETE ||
            s.status === "section2_in_progress",
        )
        ?.map((s) => ({
          ...s,
          employee: employees?.find((e) => e.id === s.userId),
          hasDocuments:
            !!s.section2?.listADocument ||
            (!!s.section2?.listBDocument && !!s.section2?.listCDocument) ||
            !!s.data?.section2,
          uploadedDocsCount: [
            s.section2?.listADocument,
            s.section2?.listBDocument,
            s.section2?.listCDocument,
          ].filter(Boolean).length,
        }))
        ?.sort((a, b) => {
          // Sort behavior: Overdue first, then by hire date
          // But also prioritize those with documents uploaded (ready to verify)
          if (a.hasDocuments !== b.hasDocuments) return b.hasDocuments ? 1 : -1;

          const dateA = new Date(
            a.employee?.profile?.hireDate || a.submittedAt,
          );
          const dateB = new Date(
            b.employee?.profile?.hireDate || b.submittedAt,
          );
          return dateA - dateB;
        }) || []
    );
  }, [submissions, employees]);

  if (pendingReviews.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900">All Caught Up!</h3>
          <p className="text-gray-500 mt-2">No pending I-9 Section 2 reviews</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
              <Inbox className="h-5 w-5 text-orange-600" />
            </div>
            <div>
              <CardTitle className="text-lg">
                I-9 Section 2 Review Queue
              </CardTitle>
              <CardDescription>
                Complete employer section within 3 business days of hire
              </CardDescription>
            </div>
          </div>
          <Badge
            variant="outline"
            className="bg-orange-50 text-orange-700 border-orange-200"
          >
            {pendingReviews.length} Pending
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Employee</TableHead>
              <TableHead>Hire Date</TableHead>
              <TableHead>Submitted</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Documents</TableHead>
              <TableHead className="text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {pendingReviews.map((review) => {
              const hireDate = new Date(
                review.employee?.profile?.hireDate ||
                  review.employee?.hireDate ||
                  review.submittedAt,
              );
              const deadline = new Date(hireDate);
              deadline.setDate(deadline.getDate() + 3);
              const daysRemaining = Math.ceil(
                (deadline - new Date()) / (1000 * 60 * 60 * 24),
              );
              const isOverdue = daysRemaining < 0;
              const isUrgent = daysRemaining <= 1 && !isOverdue;

              return (
                <TableRow
                  key={review.id}
                  className={
                    isOverdue
                      ? "bg-red-50"
                      : isUrgent
                      ? "bg-yellow-50"
                      : review.hasDocuments
                      ? "bg-blue-50"
                      : ""
                  }
                >
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-sm font-medium">
                        {review.employee?.profile?.firstName?.[0]}
                        {review.employee?.profile?.lastName?.[0]}
                      </div>
                      <div>
                        <p className="font-medium">
                          {review.employee?.profile?.firstName}{" "}
                          {review.employee?.profile?.lastName}
                        </p>
                        <p className="text-sm text-gray-500">
                          {review.employee?.email}
                        </p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>{hireDate.toLocaleDateString()}</TableCell>
                  <TableCell>
                    {review.submittedAt
                      ? new Date(review.submittedAt).toLocaleDateString()
                      : "N/A"}
                  </TableCell>
                  <TableCell>
                    {isOverdue ? (
                      <span className="text-red-600 font-medium flex items-center gap-1">
                        <AlertTriangle className="h-4 w-4" />
                        {Math.abs(daysRemaining)} days overdue
                      </span>
                    ) : isUrgent ? (
                      <span className="text-yellow-600 font-medium flex items-center gap-1">
                        <Clock className="h-4 w-4" />
                        {daysRemaining} day{daysRemaining !== 1 ? "s" : ""} left
                      </span>
                    ) : (
                      <span className="text-gray-600">
                        {daysRemaining} days left
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    {review.hasDocuments ? (
                      <Badge className="bg-green-100 text-green-700 border-green-200">
                        <FileText className="h-3 w-3 mr-1" />
                        {review.uploadedDocsCount} Uploaded
                      </Badge>
                    ) : (
                      <span className="text-gray-400 text-sm">None yet</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      size="sm"
                      onClick={() => onReview(review)}
                      className={
                        review.hasDocuments
                          ? "bg-green-600 hover:bg-green-700"
                          : isOverdue
                          ? "bg-red-600 hover:bg-red-700"
                          : ""
                      }
                    >
                      {review.hasDocuments ? "Verify Docs" : "Review"}
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

// ============================================================================
// POLICY ACKNOWLEDGMENT TRACKER
// ============================================================================

function PolicyTracker({ policies, acknowledgments, employees }) {
  const policyStats = useMemo(() => {
    return (
      policies?.map((policy) => {
        // Match by policyId, policyCode, or policy.id for compatibility
        const acks =
          acknowledgments?.filter(
            (a) =>
              a.policyId === policy.id ||
              a.policyId === policy.policyCode ||
              a.policyCode === policy.policyCode,
          ) || [];
        const completed = acks.filter(
          (a) => a.status === "acknowledged",
        ).length;
        const total =
          employees?.filter((e) => e.status === "active")?.length || 0;

        return {
          ...policy,
          completed,
          total,
          percentage: total > 0 ? Math.round((completed / total) * 100) : 0,
          pending: total - completed,
        };
      }) || []
    );
  }, [policies, acknowledgments, employees]);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
            <ClipboardCheck className="h-5 w-5 text-purple-600" />
          </div>
          <div>
            <CardTitle className="text-lg">Policy Acknowledgments</CardTitle>
            <CardDescription>
              Track employee policy acknowledgments
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {policyStats.map((policy) => (
          <div key={policy.id} className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="font-medium text-sm">{policy.title}</span>
                {policy.required && (
                  <Badge variant="outline" className="text-xs">
                    Required
                  </Badge>
                )}
              </div>
              <span className="text-sm text-gray-500">
                {policy.completed}/{policy.total} ({policy.percentage}%)
              </span>
            </div>
            <Progress value={policy.percentage} className="h-2" />
            {policy.pending > 0 && (
              <p className="text-xs text-gray-500">
                {policy.pending} employee{policy.pending !== 1 ? "s" : ""}{" "}
                pending acknowledgment
              </p>
            )}
          </div>
        ))}
      </CardContent>
      <CardFooter>
        <Button variant="outline" size="sm" className="w-full">
          <Bell className="h-4 w-4 mr-2" />
          Send Reminder to Pending Employees
        </Button>
      </CardFooter>
    </Card>
  );
}

// ============================================================================
// TRAINING COMPLIANCE TRACKER
// ============================================================================

function TrainingTracker({ requirements, assignments, employees }) {
  const trainingStats = useMemo(() => {
    return (
      requirements?.map((req) => {
        const assigns =
          assignments?.filter((a) => a.requirementId === req.id) || [];
        const completed = assigns.filter(
          (a) => a.status === "completed",
        ).length;
        const inProgress = assigns.filter(
          (a) => a.status === "in_progress",
        ).length;
        const overdue = assigns.filter((a) => {
          if (a.status === "completed") return false;
          return new Date(a.dueDate) < new Date();
        }).length;

        return {
          ...req,
          completed,
          inProgress,
          overdue,
          total: assigns.length,
        };
      }) || []
    );
  }, [requirements, assignments]);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
            <GraduationCap className="h-5 w-5 text-blue-600" />
          </div>
          <div>
            <CardTitle className="text-lg">Training Compliance</CardTitle>
            <CardDescription>
              Required training completion status
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Training</TableHead>
              <TableHead>Completed</TableHead>
              <TableHead>In Progress</TableHead>
              <TableHead>Overdue</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {trainingStats.map((training) => (
              <TableRow key={training.id}>
                <TableCell>
                  <div>
                    <p className="font-medium">{training.title}</p>
                    <p className="text-xs text-gray-500">
                      {training.frequency}
                    </p>
                  </div>
                </TableCell>
                <TableCell>
                  <Badge className="bg-green-100 text-green-700">
                    {training.completed}
                  </Badge>
                </TableCell>
                <TableCell>
                  <Badge className="bg-blue-100 text-blue-700">
                    {training.inProgress}
                  </Badge>
                </TableCell>
                <TableCell>
                  {training.overdue > 0 ? (
                    <Badge className="bg-red-100 text-red-700">
                      {training.overdue}
                    </Badge>
                  ) : (
                    <span className="text-gray-400">-</span>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

// ============================================================================
// COMPLIANCE AUDIT LOG
// ============================================================================

function AuditLog({ logs, limit = 10 }) {
  const recentLogs = useMemo(() => {
    return (
      logs
        ?.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
        ?.slice(0, limit) || []
    );
  }, [logs, limit]);

  const getActionIcon = (action) => {
    switch (action) {
      case "form_submitted":
        return <FileCheck className="h-4 w-4 text-green-600" />;
      case "document_uploaded":
        return <FileText className="h-4 w-4 text-blue-600" />;
      case "policy_acknowledged":
        return <CheckSquare className="h-4 w-4 text-purple-600" />;
      case "signature_captured":
        return <Edit className="h-4 w-4 text-orange-600" />;
      case "section2_completed":
        return <UserCheck className="h-4 w-4 text-green-600" />;
      case "training_completed":
        return <GraduationCap className="h-4 w-4 text-blue-600" />;
      case "notice_acknowledged":
        return <Scale className="h-4 w-4 text-red-600" />;
      default:
        return <Activity className="h-4 w-4 text-gray-600" />;
    }
  };

  const getActionLabel = (action) => {
    switch (action) {
      case "form_submitted":
        return "Form Submitted";
      case "document_uploaded":
        return "Document Uploaded";
      case "policy_acknowledged":
        return "Policy Acknowledged";
      case "signature_captured":
        return "Signature Captured";
      case "section2_completed":
        return "I-9 Section 2 Completed";
      case "training_completed":
        return "Training Completed";
      case "notice_acknowledged":
        return "Notice Acknowledged";
      default:
        return action;
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
              <History className="h-5 w-5 text-gray-600" />
            </div>
            <div>
              <CardTitle className="text-lg">Compliance Audit Log</CardTitle>
              <CardDescription>Recent compliance activity</CardDescription>
            </div>
          </div>
          <Button variant="outline" size="sm">
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <ScrollArea className="h-[400px]">
          <div className="space-y-4">
            {recentLogs.length === 0 ? (
              <p className="text-center text-gray-500 py-8">
                No audit log entries
              </p>
            ) : (
              recentLogs.map((log) => (
                <div
                  key={log.id}
                  className="flex items-start gap-4 pb-4 border-b last:border-0"
                >
                  <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                    {getActionIcon(log.action)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <p className="font-medium text-sm">
                        {getActionLabel(log.action)}
                      </p>
                      <span className="text-xs text-gray-500">
                        {new Date(log.timestamp).toLocaleString()}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">
                      {log.description}
                    </p>
                    <div className="flex items-center gap-2 mt-2">
                      {log.employeeName && (
                        <Badge variant="outline" className="text-xs">
                          {log.employeeName}
                        </Badge>
                      )}
                      {log.documentType && (
                        <Badge variant="outline" className="text-xs bg-blue-50">
                          {log.documentType}
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
}

// ============================================================================
// EMPLOYEE COMPLIANCE STATUS TABLE
// ============================================================================

function EmployeeComplianceTable({
  employees,
  submissions,
  onViewEmployee,
  onDownloadForms,
  externalFilter,
}) {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    if (externalFilter) {
      setStatusFilter(externalFilter);
    }
  }, [externalFilter]);

  const employeeComplianceData = useMemo(() => {
    return (
      employees?.map((emp) => {
        const empSubmissions =
          submissions?.filter((s) => s.userId === emp.id) || [];

        // Find form submissions - formType is guaranteed for all entries
        const i9 = empSubmissions.find((s) => s.formType === "I-9");
        const w4 = empSubmissions.find((s) => s.formType === "W-4");
        const stateTax = empSubmissions.find(
          (s) => s.formType === "DE-4" || s.formType.startsWith("STATE_TAX"),
        );
        const wtpn = empSubmissions.find((s) => s.formType === "WTPN");
        const caNotices = empSubmissions.find(
          (s) => s.formType === "CA_NOTICES",
        );

        const completedCount = [i9, w4, stateTax, wtpn, caNotices].filter(
          (s) => s?.status === "complete",
        ).length;
        const hasReviewItems =
          i9?.status === "section1_complete" ||
          i9?.status === "section2_in_progress" ||
          w4?.status === "pending_employer_review";

        const overallStatus = hasReviewItems
          ? "review_needed"
          : completedCount === 5
          ? "complete"
          : completedCount > 0
          ? "partial"
          : "pending";

        return {
          ...emp,
          i9: i9,
          w4: w4,
          stateTax: stateTax,
          wtpn: wtpn,
          caNotices: caNotices,
          i9Status: i9?.status || "pending",
          w4Status: w4?.status || "pending",
          stateTaxStatus: stateTax?.status || "pending",
          wtpnStatus: wtpn?.status || "pending",
          caNoticesStatus: caNotices?.status || "pending",
          completedCount,
          totalRequired: 5,
          overallStatus,
        };
      }) || []
    );
  }, [employees, submissions]);

  const filteredData = useMemo(() => {
    return employeeComplianceData.filter((emp) => {
      const matchesSearch =
        `${emp.profile?.firstName || ""} ${emp.profile?.lastName || ""}`
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        emp.email?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesStatus =
        statusFilter === "all" || emp.overallStatus === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [employeeComplianceData, searchTerm, statusFilter]);

  const getStatusBadge = (status) => {
    switch (status) {
      case "complete":
        return (
          <Badge className="bg-green-100 text-green-700">
            <CheckCircle2 className="h-3 w-3 mr-1" />
            Done
          </Badge>
        );
      case "section1_complete":
        return (
          <Badge className="bg-yellow-100 text-yellow-700">
            <Clock className="h-3 w-3 mr-1" />
            Review
          </Badge>
        );
      case "reverification_needed":
        return (
          <Badge className="bg-orange-100 text-orange-700">
            <AlertCircle className="h-3 w-3 mr-1" />
            Re-verify
          </Badge>
        );
      case "section2_overdue":
        return (
          <Badge className="bg-red-100 text-red-700">
            <AlertCircle className="h-3 w-3 mr-1" />
            Overdue
          </Badge>
        );
      case "pending_employer_review":
        return (
          <Badge className="bg-purple-100 text-purple-700">
            <Clock className="h-3 w-3 mr-1" />
            Review
          </Badge>
        );
      case "section2_in_progress":
        return (
          <Badge className="bg-blue-100 text-blue-700">
            <Clock className="h-3 w-3 mr-1" />
            Docs Uploaded
          </Badge>
        );
      case "pending_acknowledgment":
        return (
          <Badge className="bg-purple-100 text-purple-700">
            <Bell className="h-3 w-3 mr-1" />
            Pending Ack
          </Badge>
        );
      case "update_requested":
        return (
          <Badge className="bg-orange-100 text-orange-700">
            <RefreshCw className="h-3 w-3 mr-1" />
            Update Req
          </Badge>
        );
      case "draft":
        return (
          <Badge className="bg-blue-100 text-blue-700">
            <Edit className="h-3 w-3 mr-1" />
            Draft
          </Badge>
        );
      default:
        return <Badge className="bg-gray-100 text-gray-700">Pending</Badge>;
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Employee Compliance Status</CardTitle>
            <CardDescription>
              Overview of all employee form and policy completions
            </CardDescription>
          </div>
          <Button variant="outline" size="sm">
            <Download className="h-4 w-4 mr-2" />
            Export Report
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {/* Filters */}
        <div className="flex items-center gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search employees..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[200px]">
              <Filter className="h-4 w-4 mr-2" />
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="review_needed">Review Needed</SelectItem>
              <SelectItem value="complete">Complete</SelectItem>
              <SelectItem value="partial">Partial</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Employee</TableHead>
              <TableHead>Hire Date</TableHead>
              <TableHead>I-9</TableHead>
              <TableHead>W-4</TableHead>
              <TableHead>State Tax</TableHead>
              <TableHead>WTPN</TableHead>
              <TableHead>CA Notices</TableHead>
              <TableHead>Progress</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredData.map((emp) => (
              <TableRow key={emp.id}>
                <TableCell>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-sm font-medium">
                      {emp.profile?.firstName?.[0]}
                      {emp.profile?.lastName?.[0]}
                    </div>
                    <div>
                      <p className="font-medium">
                        {emp.profile?.firstName} {emp.profile?.lastName}
                      </p>
                      <p className="text-sm text-gray-500">
                        {emp.profile?.department}
                      </p>
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  {new Date(emp.profile?.hireDate).toLocaleDateString()}
                </TableCell>
                <TableCell>{getStatusBadge(emp.i9Status)}</TableCell>
                <TableCell>{getStatusBadge(emp.w4Status)}</TableCell>
                <TableCell>{getStatusBadge(emp.stateTaxStatus)}</TableCell>
                <TableCell>{getStatusBadge(emp.wtpnStatus)}</TableCell>
                <TableCell>{getStatusBadge(emp.caNoticesStatus)}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Progress
                      value={(emp.completedCount / emp.totalRequired) * 100}
                      className="w-16 h-2"
                    />
                    <span className="text-sm text-gray-500">
                      {emp.completedCount}/{emp.totalRequired}
                    </span>
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuLabel>Actions</DropdownMenuLabel>
                      <DropdownMenuItem onClick={() => onViewEmployee?.(emp)}>
                        <Eye className="h-4 w-4 mr-2" />
                        View Details
                      </DropdownMenuItem>
                      <DropdownMenuItem>
                        <Bell className="h-4 w-4 mr-2" />
                        Send Reminder
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() => onDownloadForms?.(emp)}
                        disabled={emp.completedCount === 0}
                      >
                        <Download className="h-4 w-4 mr-2" />
                        Download Forms
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

// ============================================================================
// MAIN COMPLIANCE DASHBOARD COMPONENT
// ============================================================================

export default function ComplianceDashboard() {
  const [activeTab, setActiveTab] = useState("overview");
  const [employeeTableFilter, setEmployeeTableFilter] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showEmployeeDetails, setShowEmployeeDetails] = useState(false);
  const [viewingForm, setViewingForm] = useState(null);
  const [showI9Section2Dialog, setShowI9Section2Dialog] = useState(false);
  const [selectedI9Submission, setSelectedI9Submission] = useState(null);
  const [showW4Dialog, setShowW4Dialog] = useState(false);
  const [selectedW4Submission, setSelectedW4Submission] = useState(null);

  // Query data from IndexedDB
  const employees = useLiveQuery(() => db.users?.toArray()) || [];
  const formSubmissions =
    useLiveQuery(() => db.formSubmissions?.toArray()) || [];
  const i9Submissions = useLiveQuery(() => db.i9Submissions?.toArray()) || [];
  const dbPolicies = useLiveQuery(() => db.compliancePolicies?.toArray()) || [];
  // Use static policies if no DB policies exist
  const policies =
    dbPolicies.length > 0 ? dbPolicies : Object.values(COMPANY_POLICIES);
  const policyAcks =
    useLiveQuery(() => db.policyAcknowledgments?.toArray()) || [];
  const trainingReqs =
    useLiveQuery(() => db.trainingRequirements?.toArray()) || [];
  const trainingAssigns =
    useLiveQuery(() => db.trainingAssignments?.toArray()) || [];
  const auditLogs = useLiveQuery(() => db.complianceAuditLog?.toArray()) || [];

  // Calculate statistics
  const stats = useMemo(() => {
    const totalEmployees = employees.length;
    const activeEmployees = employees.filter(
      (e) => e.status === "active",
    ).length;

    // I-9 stats
    const i9Complete = i9Submissions.filter(
      (s) => s.status === "complete",
    ).length;
    const i9PendingReview = i9Submissions.filter(
      (s) => s.status === "section1_complete",
    ).length;
    const i9Draft = i9Submissions.filter((s) => s.status === "draft").length;

    // I-9 Re-verification count
    const today = new Date();
    const ninetyDaysFromNow = new Date(today);
    ninetyDaysFromNow.setDate(ninetyDaysFromNow.getDate() + 90);

    const i9ReverificationNeeded = i9Submissions.filter((s) => {
      if (s.status !== "complete") return false;
      const data = s.data || s;
      const citizenshipStatus =
        data.citizenshipStatus || data.section1?.citizenshipStatus;
      if (
        citizenshipStatus !== "noncitizen_authorized" &&
        citizenshipStatus !== "alien_authorized"
      )
        return false;

      const expirationDate =
        data.workAuthExpiration || data.section1?.workAuthExpiration;
      if (!expirationDate) return false;

      return new Date(expirationDate) <= ninetyDaysFromNow;
    }).length;

    // Form completion rates
    const w4Complete = formSubmissions.filter(
      (s) => s.formType === "W-4" && s.status === "complete",
    ).length;
    const w4PendingReview = formSubmissions.filter(
      (s) => s.formType === "W-4" && s.status === "pending_employer_review",
    ).length;
    const stateTaxComplete = formSubmissions.filter(
      (s) =>
        (s.formType === "DE-4" || s.formType.startsWith("STATE_TAX")) &&
        s.status === "complete",
    ).length;
    const wtpnComplete = formSubmissions.filter(
      (s) => s.formType === "WTPN" && s.status === "complete",
    ).length;
    const caNoticesComplete = formSubmissions.filter(
      (s) => s.formType === "CA_NOTICES" && s.status === "complete",
    ).length;

    // Policy acks
    const totalRequiredPolicies =
      policies.filter((p) => p.required).length * totalEmployees;
    const completedPolicyAcks = policyAcks.filter(
      (a) => a.status === "acknowledged",
    ).length;
    const policyComplianceRate =
      totalRequiredPolicies > 0
        ? Math.round((completedPolicyAcks / totalRequiredPolicies) * 100)
        : 0;

    // Training
    const overdueTraining = trainingAssigns.filter((a) => {
      if (a.status === "completed") return false;
      return new Date(a.dueDate) < new Date();
    }).length;

    // Overall compliance score (now includes CA Notices)
    const totalChecks = totalEmployees * 5; // I-9, W-4, State Tax, WTPN, CA Notices
    const completedChecks =
      i9Complete +
      w4Complete +
      stateTaxComplete +
      wtpnComplete +
      caNoticesComplete;
    const overallComplianceRate =
      totalChecks > 0 ? Math.round((completedChecks / totalChecks) * 100) : 0;

    return {
      totalEmployees,
      activeEmployees,
      i9Complete,
      i9PendingReview,
      i9Draft,
      i9ReverificationNeeded,
      w4Complete,
      w4PendingReview,
      stateTaxComplete,
      wtpnComplete,
      caNoticesComplete,
      policyComplianceRate,
      overdueTraining,
      overallComplianceRate,
    };
  }, [
    employees,
    formSubmissions,
    i9Submissions,
    policies,
    policyAcks,
    trainingAssigns,
  ]);

  // Handle I-9 review
  const handleI9Review = (submission) => {
    // Open the I-9 Section 2 review dialog
    setSelectedI9Submission(submission);
    setSelectedEmployee(submission.employee);
    setShowI9Section2Dialog(true);
  };

  // Save I-9 Section 2 (draft)
  const handleSaveI9Section2 = async (data) => {
    try {
      const submission = {
        ...selectedI9Submission,
        section2: data,
        status: "section2_in_progress",
        updatedAt: new Date().toISOString(),
      };
      await db.i9Submissions.put(submission);
      console.log("Section 2 saved as draft");
    } catch (error) {
      console.error("Error saving Section 2:", error);
    }
  };

  // Complete I-9 Section 2
  const handleVerifyI9Section2 = async (data) => {
    try {
      const submission = {
        ...selectedI9Submission,
        section2: data,
        section2Complete: true,
        status: "complete",
        completedAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      await db.i9Submissions.put(submission);

      // Add audit log
      await db.complianceAuditLog.add({
        id: `log_${Date.now()}`,
        action: "i9_section2_completed",
        timestamp: new Date().toISOString(),
        userId: selectedI9Submission.userId,
        employeeName: `${selectedEmployee?.profile?.firstName || ""} ${
          selectedEmployee?.profile?.lastName || ""
        }`,
        documentType: "I-9",
        description: "I-9 Section 2 completed by employer",
        completedBy: "HR Admin", // In real app, get from auth
      });

      setShowI9Section2Dialog(false);
      setSelectedI9Submission(null);
    } catch (error) {
      console.error("Error completing Section 2:", error);
    }
  };
  // W-4 Employer Review
  const handleReviewW4 = (submission) => {
    setSelectedW4Submission(submission);
    setSelectedEmployee(employees.find((e) => e.id === submission.userId)); // Ensure employee is set for W4 dialog
    setShowW4Dialog(true);
  };

  const handleCompleteW4EmployerSection = async (data) => {
    try {
      const submission = {
        ...selectedW4Submission,
        // Merge employer data in
        data: {
          ...selectedW4Submission.data,
          employer: data.employer,
        },
        status: "complete",
        completedAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      await db.formSubmissions.put(submission);

      // Add audit log
      await db.complianceAuditLog.add({
        id: `log_w4_${Date.now()}`,
        action: "w4_employer_section_completed",
        timestamp: new Date().toISOString(),
        userId: selectedW4Submission.userId,
        employeeName: `${selectedEmployee?.profile?.firstName || ""} ${
          selectedEmployee?.profile?.lastName || ""
        }`,
        documentType: "W-4",
        description: "W-4 Employer Section completed",
        completedBy: "HR Admin",
      });

      setShowW4Dialog(false);
      setSelectedW4Submission(null);

      // Update selectedEmployee state if we are viewing the same employee
      if (
        selectedEmployee &&
        selectedEmployee.id === selectedW4Submission.userId
      ) {
        setSelectedEmployee((prev) => ({
          ...prev,
          w4: submission,
          w4Status: "complete",
        }));
      }

      toast.success("W-4 Employer Section completed");
    } catch (error) {
      console.error("Error completing W-4:", error);
      toast.error("Failed to complete W-4");
    }
  };

  // Handle I-9 Re-verification review

  // Handle I-9 Re-verification review
  const handleI9ReverificationReview = (i9Data) => {
    setSelectedI9Submission(i9Data);
    setSelectedEmployee(i9Data.employee);
    setShowI9Section2Dialog(true);
  };

  // Handle WTPN issuance
  const handleIssueWTPN = async (wtpnData) => {
    try {
      await db.formSubmissions.put(wtpnData);

      // Create notification for employee to acknowledge WTPN
      await db.notifications.add({
        id: `notif_wtpn_${wtpnData.userId}_${Date.now()}`,
        userId: wtpnData.userId,
        type: "compliance_action_required",
        title: "New Wage Theft Prevention Notice",
        message: `A new WTPN has been issued for you${
          wtpnData.reason === "pay_change" ? " due to a pay rate change" : ""
        }. Please review and acknowledge it.`,
        read: false,
        timestamp: new Date().toISOString(),
        actionUrl: "/compliance",
        actionType: "acknowledge_wtpn",
        relatedFormId: wtpnData.id,
        priority: "high",
      });

      // Add audit log
      await db.complianceAuditLog.add({
        id: `log_${Date.now()}`,
        action: "wtpn_issued",
        timestamp: new Date().toISOString(),
        userId: wtpnData.userId,
        employeeName: wtpnData.data?.employeeName,
        documentType: "WTPN",
        description: `WTPN issued for ${wtpnData.reason}: New pay rate $${wtpnData.data?.rateOfPay}`,
        completedBy: "HR Admin",
      });

      toast.success("WTPN Issued Successfully", {
        description: `${wtpnData.data?.employeeName} will receive a notification to review and acknowledge it.`,
      });
    } catch (error) {
      console.error("Error issuing WTPN:", error);
    }
  };

  // Handle form update request
  const handleRequestFormUpdate = async (request) => {
    try {
      // Find existing submission to reset, or create new placeholder
      const existingSubmission = formSubmissions.find(
        (s) => s.userId === request.userId && s.formType === request.formType,
      );

      const updatedSubmission = {
        ...(existingSubmission || {}),
        id:
          existingSubmission?.id ||
          `${request.formType}_${request.userId}_${Date.now()}`,
        formType: request.formType, // Ensure formType is set
        userId: request.userId,
        status: "update_requested", // Special status to show employee they need to update
        updatedAt: new Date().toISOString(),
        requestReason: request.reason,
        requestMessage: request.message,
        requestedBy: "HR Admin",
        requestedAt: new Date().toISOString(),
      };

      await db.formSubmissions.put(updatedSubmission);

      // Create notification for employee
      const reasonLabels = {
        marriage: "Marriage/Divorce",
        child: "Birth/Adoption of Child",
        job_change: "Second Job Change",
        income_change: "Income Change",
        annual_review: "Annual Review",
        other: "Other",
      };

      await db.notifications.add({
        id: `notif_update_${request.userId}_${Date.now()}`,
        userId: request.userId,
        type: "compliance_action_required",
        title: `Form Update Required: ${request.formType}`,
        message: `HR has requested you update your ${request.formType} form${
          request.reason
            ? ` due to: ${reasonLabels[request.reason] || request.reason}`
            : ""
        }.${request.message ? ` Note: ${request.message}` : ""}`,
        read: false,
        timestamp: new Date().toISOString(),
        actionUrl: "/compliance",
        actionType: "update_form",
        relatedFormType: request.formType,
        priority: "high",
      });

      // Store the request in audit log
      await db.complianceAuditLog.add({
        id: `log_${Date.now()}`,
        action: "form_update_requested",
        timestamp: new Date().toISOString(),
        userId: request.userId,
        employeeName: request.employeeName,
        documentType: request.formType,
        description: `${request.formType} update requested: ${
          request.reason
        }. ${request.message || ""}`,
        completedBy: "HR Admin",
      });

      console.log("Form update request sent:", request);
      toast.success("Update Request Sent", {
        description: `${request.employeeName} will receive a notification to update their ${request.formType} form.`,
      });
    } catch (error) {
      console.error("Error requesting form update:", error);
    }
  };

  // Handle bulk reminder sending
  const handleSendBulkReminders = async (
    targetEmployees,
    reminderType,
    formType,
  ) => {
    try {
      // Log the reminder action
      for (const emp of targetEmployees) {
        await db.complianceAuditLog.add({
          id: `log_${Date.now()}_${emp.id}`,
          action: "reminder_sent",
          timestamp: new Date().toISOString(),
          userId: emp.id,
          employeeName: `${emp.profile?.firstName || ""} ${
            emp.profile?.lastName || ""
          }`,
          documentType:
            reminderType === "specific_form" ? formType : "Multiple",
          description: `Compliance reminder sent for ${
            reminderType === "specific_form" ? formType : "incomplete items"
          }`,
          completedBy: "HR Admin",
        });
      }

      // In a real app, this would trigger email notifications
      console.log(`Reminders sent to ${targetEmployees.length} employees`);
      toast.success("Reminders Sent", {
        description: `Compliance reminders sent to ${
          targetEmployees.length
        } employee${targetEmployees.length !== 1 ? "s" : ""}.`,
      });
    } catch (error) {
      console.error("Error sending reminders:", error);
    }
  };

  // Handle view employee
  const handleViewEmployee = (employee) => {
    setSelectedEmployee(employee);
    setShowEmployeeDetails(true);
  };

  // Download form as JSON (can be enhanced to PDF)
  const downloadFormAsJSON = (formData, formType, employeeName) => {
    const dataStr = JSON.stringify(formData, null, 2);
    const blob = new Blob([dataStr], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${formType}_${employeeName.replace(/\s+/g, "_")}_${
      new Date().toISOString().split("T")[0]
    }.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Generate printable form HTML
  const generatePrintableForm = (formData, formType, employee) => {
    const employeeName = `${employee?.profile?.firstName || ""} ${
      employee?.profile?.lastName || ""
    }`.trim();
    const data = formData?.data || formData;
    const signature = data?.signature || formData?.signature;

    // Generate signature HTML
    const generateSignatureBlock = (sig) => {
      if (!sig)
        return '<p style="color: #9ca3af;"><em>No signature on file</em></p>';

      const sigDate = sig.timestamp
        ? new Date(sig.timestamp).toLocaleString()
        : "N/A";
      const signerName = sig.signerName || employeeName || "Employee";

      if (sig.signatureType === "drawn" && sig.signatureData) {
        // Drawn signature - render as image
        return `
          <div style="margin-top: 10px;">
            <img src="${
              sig.signatureData
            }" alt="Signature" style="max-width: 300px; max-height: 100px; border-bottom: 1px solid #000;" />
            <p style="margin-top: 5px; font-size: 12px; color: #6b7280;">
              Electronically signed by <strong>${signerName}</strong><br/>
              Date: ${sigDate}<br/>
              ${sig.ipAddress ? `IP: ${sig.ipAddress}` : ""}
            </p>
          </div>
        `;
      } else if (sig.signatureType === "typed" && sig.signatureData) {
        // Typed signature - render as styled text
        return `
          <div style="margin-top: 10px;">
            <p style="font-family: 'Brush Script MT', cursive; font-size: 28px; color: #1e3a5f; border-bottom: 1px solid #000; padding-bottom: 5px; display: inline-block;">
              ${sig.signatureData}
            </p>
            <p style="margin-top: 5px; font-size: 12px; color: #6b7280;">
              Electronically signed by <strong>${signerName}</strong><br/>
              Date: ${sigDate}<br/>
              ${sig.ipAddress ? `IP: ${sig.ipAddress}` : ""}
            </p>
          </div>
        `;
      } else if (typeof sig === "string") {
        // Legacy - just a string signature
        return `
          <div style="margin-top: 10px;">
            <p style="font-family: 'Brush Script MT', cursive; font-size: 28px; color: #1e3a5f; border-bottom: 1px solid #000; padding-bottom: 5px; display: inline-block;">
              ${sig}
            </p>
          </div>
        `;
      }

      return '<p style="color: #9ca3af;"><em>Signature format not recognized</em></p>';
    };

    let formContent = "";

    switch (formType) {
      case "W-4":
        formContent = `
          <h2>Form W-4 - Employee's Withholding Certificate</h2>
          <table>
            <tr><td><strong>Name:</strong></td><td>${data.firstName || ""} ${
          data.lastName || ""
        }</td></tr>
            <tr><td><strong>SSN:</strong></td><td>${
              data.ssn ? "***-**-" + data.ssn.slice(-4) : "N/A"
            }</td></tr>
            <tr><td><strong>Address:</strong></td><td>${
              data.address || "N/A"
            }</td></tr>
            <tr><td><strong>Filing Status:</strong></td><td>${
              data.filingStatus || "N/A"
            }</td></tr>
            <tr><td><strong>Multiple Jobs:</strong></td><td>${
              data.step2?.multipleJobs ? "Yes" : "No"
            }</td></tr>
            <tr><td><strong>Dependents Credit:</strong></td><td>$${
              data.step3?.dependentsCredit || 0
            }</td></tr>
            <tr><td><strong>Other Income:</strong></td><td>$${
              data.step4?.otherIncome || 0
            }</td></tr>
            <tr><td><strong>Deductions:</strong></td><td>$${
              data.step4?.deductions || 0
            }</td></tr>
            <tr><td><strong>Extra Withholding:</strong></td><td>$${
              data.step4?.extraWithholding || 0
            }</td></tr>
            <tr><td><strong>Status:</strong></td><td>${
              formData.status || "N/A"
            }</td></tr>
            <tr><td><strong>Submitted:</strong></td><td>${
              formData.submittedAt
                ? new Date(formData.submittedAt).toLocaleString()
                : "N/A"
            }</td></tr>
          </table>
          <div class="signature-section">
            <h3>Employee Signature</h3>
            ${generateSignatureBlock(signature)}
          </div>
        `;
        break;
      case "DE-4":
        formContent = `
          <h2>Form DE-4 - California Employee's Withholding Allowance Certificate</h2>
          <table>
            <tr><td><strong>Name:</strong></td><td>${data.firstName || ""} ${
          data.middleInitial || ""
        } ${data.lastName || ""}</td></tr>
            <tr><td><strong>SSN:</strong></td><td>${
              data.ssn ? "***-**-" + data.ssn.slice(-4) : "N/A"
            }</td></tr>
            <tr><td><strong>Address:</strong></td><td>${
              data.address?.street || ""
            }, ${data.address?.city || ""}, ${data.address?.state || ""} ${
          data.address?.zip || ""
        }</td></tr>
            <tr><td><strong>Filing Status:</strong></td><td>${
              data.filingStatus || "N/A"
            }</td></tr>
            <tr><td><strong>Regular Allowances:</strong></td><td>${
              data.regularAllowances || 0
            }</td></tr>
            <tr><td><strong>Additional Allowances:</strong></td><td>${
              data.additionalAllowances || 0
            }</td></tr>
            <tr><td><strong>Additional Withholding:</strong></td><td>$${
              data.additionalWithholding || 0
            }</td></tr>
            <tr><td><strong>Exempt:</strong></td><td>${
              data.exempt ? "Yes" : "No"
            }</td></tr>
            <tr><td><strong>Status:</strong></td><td>${
              formData.status || "N/A"
            }</td></tr>
            <tr><td><strong>Submitted:</strong></td><td>${
              formData.submittedAt
                ? new Date(formData.submittedAt).toLocaleString()
                : "N/A"
            }</td></tr>
          </table>
          <div class="signature-section">
            <h3>Employee Signature</h3>
            ${generateSignatureBlock(signature)}
          </div>
        `;
        break;
      case "I-9":
        const section1Sig =
          data?.section1?.signature || data?.signature || signature;
        formContent = `
          <h2>Form I-9 - Employment Eligibility Verification</h2>
          <h3>Section 1: Employee Information</h3>
          <table>
            <tr><td><strong>Name:</strong></td><td>${
              data.firstName || data.section1?.firstName || ""
            } ${data.lastName || data.section1?.lastName || ""}</td></tr>
            <tr><td><strong>Date of Birth:</strong></td><td>${
              data.dateOfBirth || data.section1?.dateOfBirth || "N/A"
            }</td></tr>
            <tr><td><strong>SSN:</strong></td><td>${
              data.ssn || data.section1?.ssn
                ? "***-**-" + (data.ssn || data.section1?.ssn).slice(-4)
                : "N/A"
            }</td></tr>
            <tr><td><strong>Email:</strong></td><td>${
              data.email || data.section1?.email || "N/A"
            }</td></tr>
            <tr><td><strong>Address:</strong></td><td>${
              data.address?.street || data.section1?.address?.street || ""
            }, ${data.address?.city || data.section1?.address?.city || ""}, ${
          data.address?.state || data.section1?.address?.state || ""
        } ${data.address?.zip || data.section1?.address?.zip || ""}</td></tr>
            <tr><td><strong>Citizenship Status:</strong></td><td>${
              data.citizenshipStatus ||
              data.section1?.citizenshipStatus ||
              "N/A"
            }</td></tr>
            <tr><td><strong>Status:</strong></td><td>${
              formData.status || "N/A"
            }</td></tr>
            <tr><td><strong>Submitted:</strong></td><td>${
              formData.submittedAt
                ? new Date(formData.submittedAt).toLocaleString()
                : "N/A"
            }</td></tr>
          </table>
          <div class="signature-section">
            <h3>Employee Signature (Section 1)</h3>
            ${generateSignatureBlock(section1Sig)}
          </div>
        `;
        break;
      case "WTPN":
        formContent = `
          <h2>Wage Theft Prevention Notice</h2>
          <table>
            <tr><td><strong>Employee Name:</strong></td><td>${
              data.employeeName || "N/A"
            }</td></tr>
            <tr><td><strong>Hire Date:</strong></td><td>${
              data.hireDate || "N/A"
            }</td></tr>
            <tr><td><strong>Rate of Pay:</strong></td><td>$${
              data.rateOfPay || 0
            }</td></tr>
            <tr><td><strong>Pay Basis:</strong></td><td>${
              data.payBasis || "N/A"
            }</td></tr>
            <tr><td><strong>Pay Period:</strong></td><td>${
              data.payPeriod || "N/A"
            }</td></tr>
            <tr><td><strong>Employer:</strong></td><td>${
              data.employerName || "N/A"
            }</td></tr>
            <tr><td><strong>Status:</strong></td><td>${
              formData.status || "N/A"
            }</td></tr>
            <tr><td><strong>Submitted:</strong></td><td>${
              formData.submittedAt
                ? new Date(formData.submittedAt).toLocaleString()
                : "N/A"
            }</td></tr>
          </table>
          <div class="signature-section">
            <h3>Employee Acknowledgment Signature</h3>
            ${generateSignatureBlock(signature)}
          </div>
        `;
        break;
      default:
        formContent = `<h2>${formType}</h2><pre>${JSON.stringify(
          data,
          null,
          2,
        )}</pre>`;
    }

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${formType} - ${employeeName}</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 40px; }
          h2 { color: #1e40af; border-bottom: 2px solid #1e40af; padding-bottom: 10px; }
          h3 { color: #374151; margin-top: 20px; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; }
          td { padding: 8px 12px; border-bottom: 1px solid #e5e7eb; }
          td:first-child { width: 200px; background-color: #f9fafb; }
          .header { text-align: center; margin-bottom: 30px; }
          .footer { margin-top: 40px; font-size: 12px; color: #6b7280; }
          .signature-section { margin-top: 40px; padding: 20px; border: 1px solid #e5e7eb; border-radius: 8px; background-color: #fafafa; }
          .signature-section h3 { margin-top: 0; color: #1e40af; }
          @media print { 
            body { margin: 20px; } 
            .signature-section { break-inside: avoid; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>SpaceHR Technologies Inc.</h1>
          <p>Employee Compliance Document</p>
        </div>
        ${formContent}
        <div class="footer">
          <p>Generated on ${new Date().toLocaleString()}</p>
          <p>This document is for official HR records.</p>
        </div>
      </body>
      </html>
    `;
  };

  // Print/Download form
  const handleDownloadForm = async (formData, formType, employee) => {
    // Generic File Upload Check
    if (formData?.data?.fileUrl) {
      // It's a data URL or blob URL.
      // For base64 data URLs, we can create a link.
      const link = document.createElement("a");
      link.href = formData.data.fileUrl;
      link.download =
        formData.data.fileName ||
        `${formType}_${employee?.profile?.lastName || "Employee"}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      return;
    }

    // USE NEW W-4 PDF GENERATOR
    if (formType === "W-4") {
      try {
        const loadingToast = toast.loading("Generating W-4 PDF...");

        // Ensure robust data structure for PDF generator (flatten nested data)
        const pdfData = {
          ...formData, // Keep top level fields (id, status, etc)
          ...(formData.data || {}), // Flatten nested data
          signature: formData.data?.signature || formData.signature,
          employer: formData.data?.employer || formData.employer,
        };

        const pdfBlob = await generateW4PdfFromTemplate(pdfData);

        // Create download link
        const url = URL.createObjectURL(pdfBlob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `W4_2026_${employee?.profile?.lastName || "Employee"}_${
          employee?.profile?.firstName || ""
        }.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        toast.dismiss(loadingToast);
        toast.success("W-4 PDF downloaded successfully");
      } catch (error) {
        console.error("Error generating W-4 PDF:", error);
        toast.error("Failed to generate PDF. Falling back to print view.");
        // Fallback to legacy print
        const html = generatePrintableForm(formData, formType, employee);
        const printWindow = window.open("", "_blank");
        printWindow.document.write(html);
        printWindow.document.close();
        printWindow.print();
      }
      return;
    }

    // Default legacy behavior for other forms
    const html = generatePrintableForm(formData, formType, employee);
    const printWindow = window.open("", "_blank");
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.print();
  };

  // Download all forms for an employee
  const handleDownloadAllForms = (employee) => {
    const employeeName = `${employee?.profile?.firstName || ""} ${
      employee?.profile?.lastName || ""
    }`.trim();
    const forms = [];

    if (employee.i9?.status === "complete") {
      forms.push({ type: "I-9", data: employee.i9 });
    }
    if (employee.w4?.status === "complete") {
      forms.push({ type: "W-4", data: employee.w4 });
    }
    if (employee.de4?.status === "complete") {
      forms.push({ type: "DE-4", data: employee.de4 });
    }
    if (employee.wtpn?.status === "complete") {
      forms.push({ type: "WTPN", data: employee.wtpn });
    }

    if (forms.length === 0) {
      toast.warning("No Forms Available", {
        description:
          "There are no completed forms to download for this employee.",
      });
      return;
    }

    // Generate combined HTML for all forms
    const combinedHtml = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Compliance Forms - ${employeeName}</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 40px; }
          h2 { color: #1e40af; border-bottom: 2px solid #1e40af; padding-bottom: 10px; }
          h3 { color: #374151; margin-top: 20px; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; }
          td { padding: 8px 12px; border-bottom: 1px solid #e5e7eb; }
          td:first-child { width: 200px; background-color: #f9fafb; }
          .header { text-align: center; margin-bottom: 30px; }
          .footer { margin-top: 40px; font-size: 12px; color: #6b7280; }
          .page-break { page-break-after: always; }
          .signature-section { margin-top: 40px; padding: 20px; border: 1px solid #e5e7eb; border-radius: 8px; background-color: #fafafa; }
          .signature-section h3 { margin-top: 0; color: #1e40af; }
          .form-section { margin-bottom: 40px; }
          @media print { body { margin: 20px; } .signature-section { break-inside: avoid; } }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>SpaceHR Technologies Inc.</h1>
          <p>Employee Compliance Documents</p>
          <p><strong>Employee:</strong> ${employeeName}</p>
        </div>
        ${forms
          .map((form, idx) => {
            // Extract content between body tags, excluding header and footer
            const fullHtml = generatePrintableForm(
              form.data,
              form.type,
              employee,
            );
            const contentMatch = fullHtml.match(
              /<div class="header">[\s\S]*?<\/div>([\s\S]*)<div class="footer">/,
            );
            const formContent = contentMatch ? contentMatch[1] : "";
            return `
            <div class="form-section">
              ${formContent}
            </div>
            ${idx < forms.length - 1 ? '<div class="page-break"></div>' : ""}
          `;
          })
          .join("")}
        <div class="footer">
          <p>Generated on ${new Date().toLocaleString()}</p>
          <p>This document is for official HR records.</p>
        </div>
      </body>
      </html>
    `;

    const printWindow = window.open("", "_blank");
    printWindow.document.write(combinedHtml);
    printWindow.document.close();
    printWindow.print();
  };

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            Compliance Dashboard
          </h1>
          <p className="text-gray-500 mt-1">
            Monitor and manage employee compliance activities
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button>
            <Download className="h-4 w-4 mr-2" />
            Export Report
          </Button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard
          title="Overall Compliance"
          value={`${stats.overallComplianceRate}%`}
          subtitle="Forms & policies complete"
          icon={Shield}
          color="green"
        />
        <StatCard
          title="I-9 Pending Review"
          value={stats.i9PendingReview}
          subtitle="Awaiting Section 2"
          icon={FileWarning}
          color={stats.i9PendingReview > 0 ? "orange" : "green"}
          onClick={() => setActiveTab("i9-queue")}
        />
        <StatCard
          title="I-9 Re-verification"
          value={stats.i9ReverificationNeeded}
          subtitle="Expiring within 90 days"
          icon={AlertCircle}
          color={stats.i9ReverificationNeeded > 0 ? "red" : "green"}
          onClick={() => setActiveTab("reverification")}
        />
        <StatCard
          title="Policy Compliance"
          value={`${stats.policyComplianceRate}%`}
          subtitle="Acknowledgments complete"
          icon={ClipboardCheck}
          color="purple"
        />
        <StatCard
          title="Overdue Training"
          value={stats.overdueTraining}
          subtitle="Items past due date"
          icon={GraduationCap}
          color={stats.overdueTraining > 0 ? "red" : "green"}
        />
      </div>

      {/* Main Content Tabs */}
      <Tabs
        value={activeTab}
        onValueChange={setActiveTab}
        className="space-y-6"
      >
        <TabsList className="grid w-full grid-cols-7 lg:w-auto lg:inline-grid">
          <TabsTrigger value="overview" className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="i9-queue" className="flex items-center gap-2">
            <Inbox className="h-4 w-4" />
            I-9 Queue
            {stats.i9PendingReview > 0 && (
              <Badge variant="destructive" className="ml-1">
                {stats.i9PendingReview}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger
            value="reverification"
            className="flex items-center gap-2"
          >
            <AlertCircle className="h-4 w-4" />
            Re-verify
            {stats.i9ReverificationNeeded > 0 && (
              <Badge variant="destructive" className="ml-1">
                {stats.i9ReverificationNeeded}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="wtpn" className="flex items-center gap-2">
            <DollarSign className="h-4 w-4" />
            WTPN
          </TabsTrigger>
          <TabsTrigger value="employees" className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            Employees
          </TabsTrigger>
          <TabsTrigger value="policies" className="flex items-center gap-2">
            <ClipboardCheck className="h-4 w-4" />
            Policies
          </TabsTrigger>
          <TabsTrigger value="audit" className="flex items-center gap-2">
            <History className="h-4 w-4" />
            Audit Log
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          {/* Re-verification Alerts - Show only if there are alerts */}
          <I9ReverificationAlerts
            i9Submissions={i9Submissions}
            employees={employees}
            onReview={handleI9ReverificationReview}
          />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Quick Stats */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg">
                      Form Completion Summary
                    </CardTitle>
                    <CardDescription>
                      Status of required onboarding forms
                    </CardDescription>
                  </div>
                  <BulkReminderSender
                    employees={employees}
                    formSubmissions={formSubmissions}
                    i9Submissions={i9Submissions}
                    policyAcks={policyAcks}
                    onSendReminders={handleSendBulkReminders}
                  />
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <FileText className="h-5 w-5 text-blue-600" />
                    <span>Form I-9</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Progress
                      value={(stats.i9Complete / stats.totalEmployees) * 100}
                      className="w-24 h-2"
                    />
                    <span className="text-sm text-gray-500">
                      {stats.i9Complete}/{stats.totalEmployees}
                    </span>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <FileText className="h-5 w-5 text-green-600" />
                    <span>Form W-4</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Progress
                      value={(stats.w4Complete / stats.totalEmployees) * 100}
                      className="w-24 h-2"
                    />
                    <span className="text-sm text-gray-500">
                      {stats.w4Complete}/{stats.totalEmployees}
                    </span>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <FileText className="h-5 w-5 text-amber-600" />
                    <span>State Tax</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Progress
                      value={
                        (stats.stateTaxComplete / stats.totalEmployees) * 100
                      }
                      className="w-24 h-2"
                    />
                    <span className="text-sm text-gray-500">
                      {stats.stateTaxComplete}/{stats.totalEmployees}
                    </span>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Scale className="h-5 w-5 text-red-600" />
                    <span>WTPN</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Progress
                      value={(stats.wtpnComplete / stats.totalEmployees) * 100}
                      className="w-24 h-2"
                    />
                    <span className="text-sm text-gray-500">
                      {stats.wtpnComplete}/{stats.totalEmployees}
                    </span>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Building2 className="h-5 w-5 text-purple-600" />
                    <span>CA Notices</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Progress
                      value={
                        (stats.caNoticesComplete / stats.totalEmployees) * 100
                      }
                      className="w-24 h-2"
                    />
                    <span className="text-sm text-gray-500">
                      {stats.caNoticesComplete}/{stats.totalEmployees}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Alerts */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Action Required</CardTitle>
                <CardDescription>
                  Items needing immediate attention
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {stats.i9PendingReview > 0 && (
                  <Alert
                    className="bg-orange-50 border-orange-200 cursor-pointer"
                    onClick={() => setActiveTab("i9-queue")}
                  >
                    <AlertTriangle className="h-4 w-4 text-orange-600" />
                    <AlertDescription className="text-orange-800">
                      <strong>{stats.i9PendingReview}</strong> I-9 form
                      {stats.i9PendingReview !== 1 ? "s" : ""} awaiting Section
                      2 review
                    </AlertDescription>
                  </Alert>
                )}
                {stats.w4PendingReview > 0 && (
                  <Alert
                    className="bg-purple-50 border-purple-200 cursor-pointer"
                    onClick={() => {
                      setEmployeeTableFilter("review_needed");
                      setActiveTab("employees");
                    }}
                  >
                    <FileText className="h-4 w-4 text-purple-600" />
                    <AlertDescription className="text-purple-800">
                      <strong>{stats.w4PendingReview}</strong> W-4 form
                      {stats.w4PendingReview !== 1 ? "s" : ""} awaiting employer
                      review
                    </AlertDescription>
                  </Alert>
                )}
                {stats.i9ReverificationNeeded > 0 && (
                  <Alert
                    className="bg-red-50 border-red-200 cursor-pointer"
                    onClick={() => setActiveTab("reverification")}
                  >
                    <AlertCircle className="h-4 w-4 text-red-600" />
                    <AlertDescription className="text-red-800">
                      <strong>{stats.i9ReverificationNeeded}</strong> work
                      authorization
                      {stats.i9ReverificationNeeded !== 1 ? "s" : ""} expiring
                      within 90 days
                    </AlertDescription>
                  </Alert>
                )}
                {stats.overdueTraining > 0 && (
                  <Alert className="bg-red-50 border-red-200">
                    <GraduationCap className="h-4 w-4 text-red-600" />
                    <AlertDescription className="text-red-800">
                      <strong>{stats.overdueTraining}</strong> overdue training
                      assignment{stats.overdueTraining !== 1 ? "s" : ""}
                    </AlertDescription>
                  </Alert>
                )}
                {stats.i9PendingReview === 0 &&
                  stats.w4PendingReview === 0 &&
                  stats.overdueTraining === 0 &&
                  stats.i9ReverificationNeeded === 0 && (
                    <div className="text-center py-8">
                      <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-3" />
                      <p className="text-gray-600">
                        No immediate actions required
                      </p>
                    </div>
                  )}
              </CardContent>
            </Card>
          </div>

          {/* CA Notices & Form Update Requests */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <CANoticesTracker
              submissions={formSubmissions}
              employees={employees}
            />
            <FormUpdateRequests
              employees={employees}
              submissions={formSubmissions}
              onRequestUpdate={handleRequestFormUpdate}
            />
          </div>

          {/* Training & Policy Trackers */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <PolicyTracker
              policies={policies}
              acknowledgments={policyAcks}
              employees={employees}
            />
            <TrainingTracker
              requirements={trainingReqs}
              assignments={trainingAssigns}
              employees={employees}
            />
          </div>
        </TabsContent>

        {/* I-9 Queue Tab */}
        <TabsContent value="i9-queue">
          <I9ReviewQueue
            submissions={i9Submissions}
            employees={employees}
            onReview={handleI9Review}
          />
        </TabsContent>

        {/* I-9 Re-verification Tab */}
        <TabsContent value="reverification" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                  <AlertCircle className="h-5 w-5 text-red-600" />
                </div>
                <div>
                  <CardTitle>I-9 Re-verification Required</CardTitle>
                  <CardDescription>
                    Employees with work authorization expiring within 90 days
                    need Section 3 re-verification
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <I9ReverificationAlerts
                i9Submissions={i9Submissions}
                employees={employees}
                onReview={handleI9ReverificationReview}
              />
              {stats.i9ReverificationNeeded === 0 && (
                <div className="text-center py-12">
                  <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900">
                    All Clear!
                  </h3>
                  <p className="text-gray-500 mt-2">
                    No work authorizations expiring within 90 days
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* WTPN Management Tab */}
        <TabsContent value="wtpn" className="space-y-6">
          <WTPNManager
            employees={employees}
            submissions={formSubmissions}
            onIssueWTPN={handleIssueWTPN}
          />
        </TabsContent>

        {/* Employees Tab */}
        <TabsContent value="employees">
          <EmployeeComplianceTable
            employees={employees}
            submissions={[
              ...formSubmissions,
              ...i9Submissions.map((s) => ({ ...s, formType: "I-9" })),
            ]}
            onViewEmployee={handleViewEmployee}
            onDownloadForms={handleDownloadAllForms}
            externalFilter={employeeTableFilter}
          />
        </TabsContent>

        {/* Policies Tab */}
        <TabsContent value="policies">
          <PolicyTracker
            policies={policies}
            acknowledgments={policyAcks}
            employees={employees}
          />
        </TabsContent>

        {/* Audit Log Tab */}
        <TabsContent value="audit">
          <AuditLog logs={auditLogs} limit={50} />
        </TabsContent>
      </Tabs>

      {/* Employee Details Dialog */}
      <Dialog open={showEmployeeDetails} onOpenChange={setShowEmployeeDetails}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Employee Compliance Details</DialogTitle>
            <DialogDescription>
              {selectedEmployee?.profile?.firstName}{" "}
              {selectedEmployee?.profile?.lastName}
            </DialogDescription>
          </DialogHeader>
          {selectedEmployee && (
            <div className="space-y-6">
              {/* Employee Info */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <Label className="text-sm text-gray-500">Department</Label>
                  <p className="font-medium">
                    {selectedEmployee.profile?.department || "N/A"}
                  </p>
                </div>
                <div>
                  <Label className="text-sm text-gray-500">Position</Label>
                  <p className="font-medium">
                    {selectedEmployee.profile?.position || "N/A"}
                  </p>
                </div>
                <div>
                  <Label className="text-sm text-gray-500">Hire Date</Label>
                  <p className="font-medium">
                    {selectedEmployee.profile?.hireDate
                      ? new Date(
                          selectedEmployee.profile.hireDate,
                        ).toLocaleDateString()
                      : "N/A"}
                  </p>
                </div>
                <div>
                  <Label className="text-sm text-gray-500">Email</Label>
                  <p className="font-medium">
                    {selectedEmployee.email || "N/A"}
                  </p>
                </div>
              </div>

              <Separator />

              {/* Form Status & Actions */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-4">
                  Compliance Forms
                </h3>
                <div className="space-y-3">
                  {/* I-9 */}
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <FileText className="h-5 w-5 text-blue-600" />
                      <div>
                        <p className="font-medium">Form I-9</p>
                        <p className="text-sm text-gray-500">
                          Employment Eligibility Verification
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge
                        className={
                          selectedEmployee.i9Status === "complete"
                            ? "bg-green-100 text-green-700"
                            : selectedEmployee.i9Status === "section1_complete"
                            ? "bg-yellow-100 text-yellow-700"
                            : selectedEmployee.i9Status === "draft"
                            ? "bg-blue-100 text-blue-700"
                            : "bg-gray-100 text-gray-700"
                        }
                      >
                        {selectedEmployee.i9Status === "complete"
                          ? "Complete"
                          : selectedEmployee.i9Status === "section1_complete"
                          ? "Awaiting Section 2"
                          : selectedEmployee.i9Status === "draft"
                          ? "Draft"
                          : "Pending"}
                      </Badge>
                      {selectedEmployee.i9?.status === "section1_complete" && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSelectedI9Submission(selectedEmployee.i9);
                            setShowI9Section2Dialog(true);
                          }}
                        >
                          Complete Section 2
                        </Button>
                      )}
                      {selectedEmployee.i9?.status === "complete" && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            handleDownloadForm(
                              selectedEmployee.i9,
                              "I-9",
                              selectedEmployee,
                            )
                          }
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* W-4 */}
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <FileText className="h-5 w-5 text-green-600" />
                      <div>
                        <p className="font-medium">Form W-4</p>
                        <p className="text-sm text-gray-500">
                          Federal Tax Withholding
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge
                        className={
                          selectedEmployee.w4Status === "complete"
                            ? "bg-green-100 text-green-700"
                            : selectedEmployee.w4Status === "draft"
                            ? "bg-blue-100 text-blue-700"
                            : "bg-gray-100 text-gray-700"
                        }
                      >
                        {selectedEmployee.w4Status === "complete"
                          ? "Complete"
                          : selectedEmployee.w4Status === "draft"
                          ? "Draft"
                          : selectedEmployee.w4?.status ===
                            "pending_employer_review"
                          ? "Review Needed"
                          : "Pending"}
                      </Badge>
                      {selectedEmployee.w4?.status ===
                        "pending_employer_review" && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleReviewW4(selectedEmployee.w4)}
                        >
                          Review
                        </Button>
                      )}
                      {selectedEmployee.w4?.status === "complete" && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            handleDownloadForm(
                              selectedEmployee.w4,
                              "W-4",
                              selectedEmployee,
                            )
                          }
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* DE-4 */}
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <FileText className="h-5 w-5 text-amber-600" />
                      <div>
                        <p className="font-medium">Form DE-4</p>
                        <p className="text-sm text-gray-500">
                          California State Tax Withholding
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge
                        className={
                          selectedEmployee.de4Status === "complete"
                            ? "bg-green-100 text-green-700"
                            : selectedEmployee.de4Status === "draft"
                            ? "bg-blue-100 text-blue-700"
                            : "bg-gray-100 text-gray-700"
                        }
                      >
                        {selectedEmployee.de4Status === "complete"
                          ? "Complete"
                          : selectedEmployee.de4Status === "draft"
                          ? "Draft"
                          : "Pending"}
                      </Badge>
                      {selectedEmployee.de4?.status === "complete" && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            handleDownloadForm(
                              selectedEmployee.de4,
                              "DE-4",
                              selectedEmployee,
                            )
                          }
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* WTPN */}
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <Scale className="h-5 w-5 text-red-600" />
                      <div>
                        <p className="font-medium">
                          Wage Theft Prevention Notice
                        </p>
                        <p className="text-sm text-gray-500">
                          California Labor Code §2810.5
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge
                        className={
                          selectedEmployee.wtpnStatus === "complete"
                            ? "bg-green-100 text-green-700"
                            : selectedEmployee.wtpnStatus === "draft"
                            ? "bg-blue-100 text-blue-700"
                            : "bg-gray-100 text-gray-700"
                        }
                      >
                        {selectedEmployee.wtpnStatus === "complete"
                          ? "Complete"
                          : selectedEmployee.wtpnStatus === "draft"
                          ? "Draft"
                          : "Pending"}
                      </Badge>
                      {selectedEmployee.wtpn?.status === "complete" && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            handleDownloadForm(
                              selectedEmployee.wtpn,
                              "WTPN",
                              selectedEmployee,
                            )
                          }
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Completed Form Details */}
              {(selectedEmployee.w4?.status === "complete" ||
                selectedEmployee.de4?.status === "complete") && (
                <>
                  <Separator />
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-4">
                      Completed Form Data
                    </h3>

                    {selectedEmployee.w4?.status === "complete" && (
                      <div className="mb-4 p-4 bg-green-50 rounded-lg border border-green-200">
                        <h4 className="font-medium text-green-800 mb-2">
                          W-4 Details
                        </h4>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <div>
                            <span className="text-gray-600">
                              Filing Status:
                            </span>{" "}
                            <span className="font-medium">
                              {selectedEmployee.w4.data?.filingStatus || "N/A"}
                            </span>
                          </div>
                          <div>
                            <span className="text-gray-600">Submitted:</span>{" "}
                            <span className="font-medium">
                              {selectedEmployee.w4.submittedAt
                                ? new Date(
                                    selectedEmployee.w4.submittedAt,
                                  ).toLocaleDateString()
                                : "N/A"}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}

                    {selectedEmployee.de4?.status === "complete" && (
                      <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                        <h4 className="font-medium text-amber-800 mb-2">
                          DE-4 Details
                        </h4>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <div>
                            <span className="text-gray-600">
                              Filing Status:
                            </span>{" "}
                            <span className="font-medium">
                              {selectedEmployee.de4.data?.filingStatus || "N/A"}
                            </span>
                          </div>
                          <div>
                            <span className="text-gray-600">Allowances:</span>{" "}
                            <span className="font-medium">
                              {selectedEmployee.de4.data?.regularAllowances ||
                                0}
                            </span>
                          </div>
                          <div>
                            <span className="text-gray-600">Submitted:</span>{" "}
                            <span className="font-medium">
                              {selectedEmployee.de4.submittedAt
                                ? new Date(
                                    selectedEmployee.de4.submittedAt,
                                  ).toLocaleDateString()
                                : "N/A"}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setShowEmployeeDetails(false)}
            >
              Close
            </Button>
            {selectedEmployee && selectedEmployee.completedCount > 0 && (
              <Button onClick={() => handleDownloadAllForms(selectedEmployee)}>
                <Download className="h-4 w-4 mr-2" />
                Download All Forms
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* W-4 Employer Review Dialog */}
      <Dialog
        open={showW4Dialog}
        onOpenChange={(open) => {
          setShowW4Dialog(open);
          if (!open) setSelectedW4Submission(null);
        }}
      >
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          {selectedW4Submission && selectedEmployee && (
            <FormW4
              employee={selectedEmployee}
              existingSubmission={
                selectedW4Submission?.data
                  ? {
                      ...selectedW4Submission.data,
                      status: selectedW4Submission.status,
                      id: selectedW4Submission.id,
                      userId: selectedW4Submission.userId,
                    }
                  : selectedW4Submission
              }
              isAdmin={true}
              onSubmit={handleCompleteW4EmployerSection}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* I-9 Section 2 Review Dialog */}
      <Dialog
        open={showI9Section2Dialog}
        onOpenChange={(open) => {
          setShowI9Section2Dialog(open);
          if (!open) {
            setSelectedI9Submission(null);
          }
        }}
      >
        <DialogContent className="max-w-5xl max-h-[95vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-blue-600" />
              I-9 Section 2: Employer Review and Verification
            </DialogTitle>
            <DialogDescription>
              Review the employee's documents and complete Section 2. You have 3
              business days from their start date.
            </DialogDescription>
          </DialogHeader>

          {selectedI9Submission && selectedEmployee && (
            <div className="space-y-4">
              {/* Employee Summary */}
              <Alert className="bg-blue-50 border-blue-200">
                <UserCheck className="h-4 w-4 text-blue-600" />
                <AlertTitle className="text-blue-800">
                  Employee Information
                </AlertTitle>
                <AlertDescription className="text-blue-700">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-2">
                    <div>
                      <span className="text-blue-600 text-xs">Name:</span>
                      <p className="font-medium">
                        {selectedEmployee?.profile?.firstName}{" "}
                        {selectedEmployee?.profile?.lastName}
                      </p>
                    </div>
                    <div>
                      <span className="text-blue-600 text-xs">
                        Section 1 Submitted:
                      </span>
                      <p className="font-medium">
                        {selectedI9Submission.submittedAt
                          ? new Date(
                              selectedI9Submission.submittedAt,
                            ).toLocaleDateString()
                          : "N/A"}
                      </p>
                    </div>
                    <div>
                      <span className="text-blue-600 text-xs">Hire Date:</span>
                      <p className="font-medium">
                        {selectedEmployee?.profile?.hireDate
                          ? new Date(
                              selectedEmployee.profile.hireDate,
                            ).toLocaleDateString()
                          : "N/A"}
                      </p>
                    </div>
                    <div>
                      <span className="text-blue-600 text-xs">
                        Citizenship Status:
                      </span>
                      <p className="font-medium">
                        {selectedI9Submission.data?.citizenshipStatus ||
                          selectedI9Submission.section1?.citizenshipStatus ||
                          "N/A"}
                      </p>
                    </div>
                  </div>
                </AlertDescription>
              </Alert>

              {/* Instructions */}
              <Alert className="bg-amber-50 border-amber-200">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                <AlertTitle className="text-amber-800">
                  Important Instructions
                </AlertTitle>
                <AlertDescription className="text-amber-700 text-sm">
                  <ol className="list-decimal ml-4 mt-2 space-y-1">
                    <li>
                      <strong>Physically examine</strong> the original documents
                      presented by the employee (not copies)
                    </li>
                    <li>
                      <strong>Verify</strong> the documents appear genuine and
                      relate to the person presenting them
                    </li>
                    <li>
                      <strong>Record</strong> the document title, issuing
                      authority, number, and expiration date
                    </li>
                    <li>
                      <strong>Sign</strong> to certify you examined the
                      documents
                    </li>
                  </ol>
                </AlertDescription>
              </Alert>

              {/* Form I-9 with isEmployer=true for Section 2 */}
              <FormI9
                employee={selectedEmployee}
                existingSubmission={{
                  ...selectedI9Submission,
                  section1:
                    selectedI9Submission.data || selectedI9Submission.section1,
                  section1Complete: true,
                }}
                isEmployer={true}
                onSaveSection2={handleSaveI9Section2}
                onVerifySection2={handleVerifyI9Section2}
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
